// Short Danish explanations for Microsoft Graph / app permissions. The
// permission NAME itself (Application.ReadWrite.All …) is never translated —
// only the explanation. Graph only returns English descriptions, so this
// builds a short Danish sentence from the name: an exact text for the
// well-known ones, otherwise <what it may do> + <what it applies to>.

const EXACT = {
  full_access_as_app: 'Fuld adgang til alle postkasser uden at en bruger er logget ind.',
  'Exchange.ManageAsApp': 'Kan administrere Exchange uden at en bruger er logget ind.',
  'Application.ReadWrite.All': 'Kan oprette og ændre alle apps og deres adgang.',
  'Directory.ReadWrite.All': 'Kan læse og ændre alle brugere, grupper og indstillinger.',
  'RoleManagement.ReadWrite.Directory': 'Kan give administratorroller til sig selv og andre.',
  'AppRoleAssignment.ReadWrite.All': 'Kan give apps adgang til andre apps og data.',
  'Policy.ReadWrite.ConditionalAccess': 'Kan ændre loginreglerne (Conditional Access).',
  'Mail.ReadWrite': 'Kan læse, ændre og slette mail i alle postkasser.',
  'Mail.Send': 'Kan sende mail som enhver bruger.',
  'Mail.Read': 'Kan læse mail i alle postkasser.',
  'Files.ReadWrite.All': 'Kan læse og ændre alle filer i OneDrive og SharePoint.',
  'Files.Read.All': 'Kan læse alle filer i OneDrive og SharePoint.',
  'Sites.FullControl.All': 'Har fuld kontrol over alle SharePoint-sites.',
  'Sites.ReadWrite.All': 'Kan læse og ændre alle SharePoint-sites.',
  'Sites.Read.All': 'Kan læse alle SharePoint-sites.',
  'User.ReadWrite.All': 'Kan ændre alle brugeres profiloplysninger.',
  'User.Read.All': 'Kan læse alle brugeres profiloplysninger.',
  'Group.ReadWrite.All': 'Kan læse og ændre alle grupper og deres medlemmer.',
  'Group.Read.All': 'Kan læse alle grupper og deres medlemmer.',
  'Directory.Read.All': 'Kan læse alle brugere, grupper og indstillinger.',
  'AuditLog.Read.All': 'Kan læse auditloggen over hvem der har gjort hvad.',
  'Reports.Read.All': 'Kan læse brugs- og aktivitetsrapporter.',
  'Organization.Read.All': 'Kan læse organisationens oplysninger og licenser.',
  'Calendars.Read': 'Kan læse alle kalendere.',
  'Calendars.ReadWrite': 'Kan læse og ændre alle kalendere.',
  'Contacts.Read': 'Kan læse alle kontakter.',
  'User.EnableDisableAccount.All': 'Kan aktivere og deaktivere brugerkonti.',
  'User.Export.All': 'Kan eksportere brugeres data.',
  'User.Invite.All': 'Kan invitere gæstebrugere udefra.',
  'User.ManageIdentities.All': 'Kan administrere brugeres loginidentiteter.',
  'DeviceManagementManagedDevices.PrivilegedOperations.All': 'Kan udføre kritiske handlinger på enheder, fx slette og nulstille.',
  'Policy.ReadWrite.AllowSelfServicePurchase': 'Kan ændre, om brugere selv må købe licenser.',
};

// What each Policy.* permission is about.
const POLICY_TOPIC = {
  AccessReview: 'adgangsgennemgange',
  ApplicationConfiguration: 'appkonfiguration',
  AuthenticationFlows: 'loginflows',
  AuthenticationMethod: 'loginmetoder',
  Authorization: 'autorisation',
  ConditionalAccess: 'loginregler',
  ConsentRequest: 'samtykkeanmodninger',
  CrossTenantAccess: 'adgang på tværs af tenants',
  DeviceConfiguration: 'enhedskonfiguration',
  ExternalIdentities: 'eksterne identiteter',
  FeatureRollout: 'udrulning af funktioner',
  PermissionGrant: 'godkendte tilladelser',
  TrustFramework: 'trust framework',
};

const NOUN = {
  User: 'brugere',
  Group: 'grupper',
  GroupMember: 'gruppemedlemmer',
  Directory: 'brugere, grupper og indstillinger',
  Mail: 'mail',
  MailboxSettings: 'postkasseindstillinger',
  Calendars: 'kalendere',
  Contacts: 'kontakter',
  Files: 'filer',
  Sites: 'SharePoint-sites',
  Notes: 'OneNote-noter',
  Tasks: 'opgaver',
  Team: 'teams',
  TeamMember: 'teammedlemmer',
  TeamSettings: 'teamindstillinger',
  Channel: 'kanaler',
  ChannelMessage: 'kanalbeskeder',
  Chat: 'chats',
  ChatMessage: 'chatbeskeder',
  Application: 'apps',
  AppRoleAssignment: 'apprettigheder',
  Policy: 'politikker',
  RoleManagement: 'administratorroller',
  Device: 'enheder',
  DeviceManagementApps: 'Intune-apps',
  DeviceManagementConfiguration: 'Intune-konfiguration',
  DeviceManagementManagedDevices: 'administrerede enheder',
  DeviceManagementServiceConfig: 'Intune-indstillinger',
  DeviceManagementRBAC: 'Intune-roller',
  Organization: 'organisationens oplysninger',
  Reports: 'rapporter',
  AuditLog: 'auditlog',
  SecurityEvents: 'sikkerhedshændelser',
  SecurityAlert: 'sikkerhedsalarmer',
  SecurityIncident: 'sikkerhedshændelser',
  ThreatIndicators: 'trusselsindikatorer',
  Domain: 'domæner',
  IdentityRiskEvent: 'risikohændelser',
  IdentityRiskyUser: 'risikobrugere',
  Presence: 'tilstedeværelse',
  Printer: 'printere',
  LicenseAssignment: 'licenser',
  Subscription: 'abonnementer',
  ActivityFeed: 'aktivitetsdata',
  ServiceHealth: 'servicestatus',
  ServiceMessage: 'servicebeskeder',
  UserAuthenticationMethod: 'loginmetoder',
  Machine: 'enheder',
  Alert: 'alarmer',
  Vulnerability: 'sårbarheder',
  Software: 'software',
  Incident: 'hændelser',
  Ti: 'trusselsindikatorer',
  Exchange: 'Exchange',
  Directory_: 'mappen',
  Sharing: 'delingsindstillinger',
  TermStore: 'SharePoints termer',
  Tenant: 'tenanten',
  discovery: 'Cloud Discovery',
  investigation: 'undersøgelser',
  settings: 'indstillinger',
  Score: 'sikkerhedsscore',
  CloudPC: 'Cloud PC-enheder',
  CustomDetection: 'egne detektionsregler',
  AiEnterpriseInteraction: 'AI-interaktioner',
  AttackSimulation: 'angrebssimuleringer',
  AuditLogsQuery: 'auditlog-forespørgsler',
  'CloudApp-Discovery': 'opdagede cloud-apps',
  IdentityRiskyServicePrincipal: 'risikofyldte apps',
  IdentityUserFlow: 'brugerflows',
  RecordsManagement: 'arkivering',
  ReportSettings: 'rapportindstillinger',
  SecurityActions: 'sikkerhedshandlinger',
  SensitivityLabels: 'følsomhedsmærkater',
  SharePointTenantSettings: 'SharePoint-indstillinger',
  ThreatHunting: 'trusselsjagt',
  'User-PasswordProfile': 'brugeres adgangskodeprofil',
  DeviceManagementScripts: 'Intune-scripts',
};

function verbFor(action) {
  if (/FullControl/i.test(action)) return 'Har fuld kontrol over';
  if (/ReadWrite/i.test(action)) return 'Kan læse og ændre';
  if (/^Read/i.test(action)) return 'Kan læse';
  if (/Write|Update/i.test(action)) return 'Kan ændre';
  if (/Send/i.test(action)) return 'Kan sende';
  if (/Manage/i.test(action)) return 'Kan administrere';
  if (/Delete/i.test(action)) return 'Kan slette';
  if (/Create/i.test(action)) return 'Kan oprette';
  return 'Har adgang til';
}

// Never throws — anything it can't read still gets a short Danish sentence.
export function permissionDescription(name) {
  if (!name) return '';
  if (EXACT[name]) return EXACT[name];
  const [head, action = '', ...rest] = name.split('.');
  const verb = verbFor(action);
  if (head === 'Policy' && rest[0] && POLICY_TOPIC[rest[0]]) return `${verb} politikker for ${POLICY_TOPIC[rest[0]]}.`;
  // A name we have no word for: split the camelCase so it at least reads
  // as words ("AttackSimulation" -> "attack simulation").
  const noun = NOUN[head] || head.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  const scope = rest.includes('All') ? ' i hele tenanten' : rest.includes('OwnedBy') ? ' (kun dem appen selv ejer)' : '';
  return `${verb} ${noun}${scope}.`;
}
